/**
 * Сервис для отображения Snackbar уведомлений
 * Использует Material-UI Snackbar для показа уведомлений
 */

import i18next from 'i18next';
import { SnackbarContent, Alert, AlertColor } from "@mui/material";

const $t = i18next.t.bind(i18next);

const STATUS_I18N_KEYS: Record<string, string> = {
  pending: "order_status_pending",
  waiting_approval: "order_status_waiting_approval",
  assigned: "order_status_assigned",
  in_progress: "order_status_in_progress",
  completed: "order_status_completed",
  confirmed: "order_status_confirmed",
  cancelled: "order_status_cancelled",
  archived: "order_status_archived",
  cancelled_by_customer: "status_cancelled_by_customer",
  cancelled_by_admin: "status_cancelled_by_admin",
};

// For navigation — import the router
let globalNavigate: (path: string) => void;
let globalOpenOrderModal: (orderId: number) => void;

export function setGlobalNavigate(navigateFn: (path: string) => void) {
  globalNavigate = navigateFn;
}

export function setGlobalOpenOrderModal(
  fn: (orderId: number) => void,
) {
  globalOpenOrderModal = fn;
}

export function openOrderModal(orderId: number) {
  if (globalOpenOrderModal) {
    globalOpenOrderModal(orderId);
  } else if (globalNavigate) {
    globalNavigate(`/orders/${orderId}`);
  } else {
    window.location.href = `/orders/${orderId}`;
  }
}

export interface NotificationData {
  id?: string | number;
  type:
    | "order_available"
    | "order_status_changed"
    | "order_assigned"
    | "order_cancelled"
    | "order_completed"
    | "performer_responded"
    | "system"
    | "info"
    | "success"
    | "warning"
    | "error";
  title: string;
  message: string;
  priority?: "low" | "normal" | "high" | "urgent";
  actionLabel?: string;
  onAction?: () => void;
  duration?: number;
}

class NotificationSnackbarService {
  private static instance: NotificationSnackbarService;
  private listeners: Array<(notification: NotificationData) => void> = [];
  private audioContext: AudioContext | null = null;
  private audioContextInitialized = false;

  private constructor() {
    // AudioContext will be initialized lazily on first user interaction
    // to comply with browser autoplay policy
  }

  private initAudioContext(): void {
    if (this.audioContextInitialized) return;
    try {
      this.audioContext = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
      this.audioContextInitialized = true;
    } catch (e) {
      console.warn("Web Audio API not supported", e);
    }
  }

  private async ensureAudioContext(): Promise<AudioContext | null> {
    if (!this.audioContext) {
      this.initAudioContext();
    }
    if (this.audioContext && this.audioContext.state === "suspended") {
      try {
        await this.audioContext.resume();
      } catch (e) {
        console.warn("Could not resume AudioContext", e);
      }
    }
    return this.audioContext;
  }

  public static getInstance(): NotificationSnackbarService {
    if (!NotificationSnackbarService.instance) {
      NotificationSnackbarService.instance = new NotificationSnackbarService();
    }
    return NotificationSnackbarService.instance;
  }

  /**
   * Подписка на уведомления
   */
  public subscribe(
    callback: (notification: NotificationData) => void,
  ): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(
        (listener) => listener !== callback,
      );
    };
  }

  /**
   * Показать уведомление
   */
  public show(notification: NotificationData): void {
    console.log("[NotificationSnackbar] Showing notification:", notification);

    // Уведомляем всех слушателей
    this.listeners.forEach((listener) => {
      try {
        listener(notification);
      } catch (error) {
        console.error("[NotificationSnackbar] Error in listener:", error);
      }
    });

    // Воспроизводим звук (не ждём завершения)
    this.playNotificationSound(notification.type, notification.priority);

    // Вибрация (для мобильных устройств)
    this.vibrate(notification.type);

    // Browser notification если разрешено
    this.showBrowserNotification(notification);
  }

  /**
   * Показать уведомление о новом заказе
   */
  public showOrderAvailable(orderData: {
    id: number;
    title: string;
    address: string;
    price: number;
  }): void {
    this.show({
      type: "order_available",
      title: $t("notif_new_order"),
      message: `${orderData.title}: ${orderData.address} - ${orderData.price} ₽`,
      priority: "high",
      actionLabel: $t("notif_view"),
      onAction: () => {
        if (globalOpenOrderModal) {
          globalOpenOrderModal(orderData.id);
        } else if (globalNavigate) {
          globalNavigate(`/orders/${orderData.id}`);
        } else {
          window.location.href = `/orders/${orderData.id}`;
        }
      },
      duration: 60000,
    });
  }

  /**
   * Показать уведомление об изменении статуса заказа
   */
  public showOrderStatusChanged(
    orderId: number,
    orderTitle: string,
    newStatus: string,
    statusRu?: string,
  ): void {
    const displayStatus =
      statusRu && !STATUS_I18N_KEYS[statusRu]
        ? statusRu
        : $t(STATUS_I18N_KEYS[newStatus] || "order_status_pending");

    this.show({
      type: "order_status_changed",
      title: $t("notif_order_status_changed"),
      message: `${orderTitle}: ${displayStatus}`,
      priority: "normal",
      actionLabel: $t("notif_view"),
      onAction: () => {
        if (globalOpenOrderModal) {
          globalOpenOrderModal(orderId);
        } else if (globalNavigate) {
          globalNavigate(`/orders/${orderId}`);
        } else {
          window.location.href = `/orders/${orderId}`;
        }
      },
      duration: 60000,
    });
  }

  /**
   * Показать уведомление о назначении заказа
   */
  public showOrderAssigned(orderId: number, orderTitle: string): void {
    this.show({
      type: "order_assigned",
      title: $t("notif_order_assigned"),
      message: $t("notif_assigned_message", { orderTitle }),
      priority: "high",
      actionLabel: $t("notif_go_to_order"),
      onAction: () => {
        if (globalOpenOrderModal) {
          globalOpenOrderModal(orderId);
        } else if (globalNavigate) {
          globalNavigate(`/orders/${orderId}`);
        } else {
          window.location.href = `/orders/${orderId}`;
        }
      },
      duration: 60000,
    });
  }

  /**
   * Показать уведомление об отмене заказа
   */
  public showOrderCancelled(
    orderId: number,
    orderTitle: string,
    reason?: string,
  ): void {
    this.show({
      type: "order_cancelled",
      title: $t("notif_order_cancelled"),
      message: `${orderTitle}${reason ? `: ${reason}` : ""}`,
      priority: "high",
      actionLabel: $t("notif_view"),
      onAction: () => {
        if (globalOpenOrderModal) {
          globalOpenOrderModal(orderId);
        } else if (globalNavigate) {
          globalNavigate(`/orders/${orderId}`);
        } else {
          window.location.href = `/orders/${orderId}`;
        }
      },
      duration: 60000,
    });
  }

  /**
   * Показать уведомление о завершении заказа
   */
  public showOrderCompleted(orderId: number, orderTitle: string): void {
    this.show({
      type: "order_completed",
      title: $t("notif_order_completed"),
      message: $t("notif_completed_message", { orderTitle }),
      priority: "normal",
      actionLabel: $t("notif_view"),
      onAction: () => {
        if (globalOpenOrderModal) {
          globalOpenOrderModal(orderId);
        } else if (globalNavigate) {
          globalNavigate(`/orders/${orderId}`);
        } else {
          window.location.href = `/orders/${orderId}`;
        }
      },
      duration: 60000,
    });
  }

  /**
   * Показать уведомление об отклике исполнителя на заказ (для клиента)
   */
  public showPerformerResponded(
    orderId: number,
    performerName: string,
    orderTitle: string,
    performerRating?: number,
    performerCompletedOrders?: number,
  ): void {
    const stars = performerRating
      ? "⭐".repeat(Math.round(performerRating))
      : "";
    const experience = performerCompletedOrders
      ? ` (${performerCompletedOrders} ${$t("notif_orders_abbr")})`
      : "";

    this.show({
      type: "performer_responded",
      title: $t("notif_new_response"),
      message: $t("notif_response_message", { performerName, stars, experience, orderTitle }),
      priority: "high",
      actionLabel: $t("notif_select_performer"),
      onAction: () => {
        if (globalOpenOrderModal) {
          globalOpenOrderModal(orderId);
        } else if (globalNavigate) {
          globalNavigate(`/orders/${orderId}`);
        } else {
          window.location.href = `/orders/${orderId}`;
        }
      },
      duration: 60000, // 10 секунд - важное уведомление
    });
  }

  /**
   * Показать информационное уведомление
   */
  public showInfo(
    title: string,
    message: string,
    actionLabel?: string,
    onAction?: () => void,
  ): void {
    this.show({
      type: "info",
      title,
      message,
      priority: "low",
      actionLabel,
      onAction,
      duration: 60000,
    });
  }

  /**
   * Показать уведомление об успехе
   */
  public showSuccess(title: string, message: string): void {
    this.show({
      type: "success",
      title: title,
      message,
      priority: "low",
      duration: 60000,
    });
  }

  /**
   * Показать предупреждение
   */
  public showWarning(title: string, message: string): void {
    this.show({
      type: "warning",
      title: title,
      message,
      priority: "normal",
      duration: 60000,
    });
  }

  /**
   * Показать уведомление об ошибке
   */
  public showError(title: string, message: string): void {
    this.show({
      type: "error",
      title: title,
      message,
      priority: "high",
      duration: 60000,
    });
  }

  /**
   * Воспроизведение звука уведомления
   */
  private async playNotificationSound(type: string, priority?: string): Promise<void> {
    // Проверяем, включен ли звук в настройках
    const soundEnabled =
      localStorage.getItem("notification_sound_enabled") !== "false";
    if (!soundEnabled) return;

    // Проверяем рабочие часы
    if (!this.isWithinWorkHours()) return;

    try {
      // Используем Web Audio API для генерации звука
      const audioContext = await this.ensureAudioContext();
      if (!audioContext) return;

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Разные тона для разных типов уведомлений
      const frequencies: Record<string, number> = {
        order_available: 880, // A5
        order_status_changed: 660, // E5
        order_assigned: 880,
        order_cancelled: 440, // A4 (ниже)
        order_completed: 1046, // C6
        error: 330,
        warning: 523,
        success: 784,
      };

      const baseFreq = frequencies[type] || 660;
      const priorityMultiplier =
        priority === "urgent" ? 1.5 : priority === "high" ? 1.2 : 1;

      oscillator.frequency.value = baseFreq * priorityMultiplier;
      oscillator.type = "sine";

      // Паттерны для разных типов
      const patterns: Record<string, number[]> = {
        order_available: [150, 100, 150], // Двойной сигнал
        order_status_changed: [200], // Одиночный
        order_assigned: [150, 100, 150],
        order_cancelled: [100, 50, 100, 50, 100], // Тройной
        order_completed: [300],
        error: [400],
        warning: [250],
        success: [200, 100, 200],
      };

      const pattern = patterns[type] || [200];
      const now = audioContext.currentTime;

      pattern.forEach((duration, index) => {
        const startTime =
          now + pattern.slice(0, index).reduce((a, b) => a + b + 50, 0) / 1000;
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          startTime + duration / 1000,
        );
      });

      oscillator.start(now);
      oscillator.stop(now + pattern.reduce((a, b) => a + b + 50, 0) / 1000);
    } catch (error) {
      console.warn("[NotificationSnackbar] Could not play sound:", error);
    }
  }

  /**
   * Вибрация устройства
   */
  private vibrate(type: string): void {
    if (!("vibrate" in navigator)) return;

    const vibrationEnabled =
      localStorage.getItem("notification_vibration_enabled") !== "false";
    if (!vibrationEnabled) return;

    if (!this.isWithinWorkHours()) return;

    // Паттерны вибрации
    const patterns: Record<string, number | number[]> = {
      order_available: [200, 100, 200], // Двойная
      order_status_changed: [300], // Одиночная
      order_assigned: [200, 100, 200],
      order_cancelled: [100, 50, 100, 50, 100], // Тройная
      order_completed: [400],
      error: [500],
      warning: [250],
      success: [200, 100, 200],
    };

    const pattern = patterns[type] || [200];
    navigator.vibrate(pattern);
  }

  /**
   * Показ браузерного уведомления
   */
  private showBrowserNotification(notification: NotificationData): void {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;

    const browserNotificationEnabled =
      localStorage.getItem("browser_notification_enabled") !== "false";
    if (!browserNotificationEnabled) return;

    if (!this.isWithinWorkHours()) return;

    try {
      const browserNotif = new Notification(notification.title, {
        body: notification.message,
        icon: "/dispatcher_icon.webp",
        tag: `notification-${notification.id || Date.now()}`,
        requireInteraction:
          notification.priority === "high" ||
          notification.priority === "urgent",
        data: notification, // Сохраняем данные для обработки клика
      });

      // Обработка клика на browser notification
      browserNotif.onclick = () => {
        console.log("[NotificationSnackbar] Browser notification clicked");
        window.focus(); // Фокусируем окно браузера

        // Навигация к заказу если есть order_id
        if (notification.onAction) {
          notification.onAction();
        }

        browserNotif.close();
      };
    } catch (error) {
      console.warn(
        "[NotificationSnackbar] Could not show browser notification:",
        error,
      );
    }
  }

  /**
   * Проверка рабочих часов
   */
  private isWithinWorkHours(): boolean {
    const workHoursEnabled =
      localStorage.getItem("work_hours_enabled") === "true";
    if (!workHoursEnabled) return true;

    const startHour = parseInt(localStorage.getItem("work_start_hour") || "9");
    const startMinute = parseInt(
      localStorage.getItem("work_start_minute") || "0",
    );
    const endHour = parseInt(localStorage.getItem("work_end_hour") || "18");
    const endMinute = parseInt(localStorage.getItem("work_end_minute") || "0");

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const startTime = startHour * 60 + startMinute;
    const endTime = endHour * 60 + endMinute;

    if (startTime <= endTime) {
      return currentTime >= startTime && currentTime <= endTime;
    } else {
      // Рабочее время пересекает полночь
      return currentTime >= startTime || currentTime <= endTime;
    }
  }

  /**
   * Запрос разрешения на браузерные уведомления
   */
  public async requestPermission(): Promise<boolean> {
    if (!("Notification" in window)) {
      console.warn("[NotificationSnackbar] Notifications not supported");
      return false;
    }

    if (Notification.permission === "granted") {
      return true;
    }

    if (Notification.permission === "denied") {
      console.warn("[NotificationSnackbar] Notifications permission denied");
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    } catch (error) {
      console.error(
        "[NotificationSnackbar] Error requesting permission:",
        error,
      );
      return false;
    }
  }

  /**
   * Инициализация AudioContext при взаимодействии пользователя
   */
  public initAudioContextOnInteraction(): void {
    this.initAudioContext();
  }
}

// Экспортируем singleton instance
export const notificationSnackbarService =
  NotificationSnackbarService.getInstance();
export default notificationSnackbarService;
